import List from '@mui/material/List'
import React, { useCallback, useState } from 'react'

import {
  useCreateWorkOrderNoteMutation,
  useDeleteWorkOrderNoteMutation,
  useLazyGetWorkOrderNoteHistoryQuery,
  usePatchWorkOrderNoteMutation,
  useUpdateWorkOrderNoteNewMutation,
} from 'api/notes/workOrderNotesCrud'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import { NOTE_TYPES } from 'settings/constants/note'
import T from 'T'

import AddUpdateNoteDialog from '../common/AddUpdateNoteDialog'
import NoteActionMenu from '../common/NoteActionMenu'
import NoteListItem from '../common/NoteListItem'
import ViewNoteDialog from '../common/ViewNoteDialog'

const { WORK_ORDER } = NOTE_TYPES

type NoteRecord = Record<string, unknown> & { id?: string }

type WONotesProps = {
  notes?: NoteRecord[]
  workOrderId?: string
  noNotesText?: string
  sx?: object
  itemSx?: object
}

type WONoteListItemRowProps = {
  note: NoteRecord
  index: number
  notesLength: number
  itemSx?: object
  onOpenView: (note: NoteRecord) => void
  onOpenActionMenu: (event: React.MouseEvent, note: NoteRecord) => void
}

const WONoteListItemRow: React.FC<WONoteListItemRowProps> = ({ note, index, notesLength, itemSx, onOpenView, onOpenActionMenu }) => {
  const handleViewClick = useCallback(() => {
    onOpenView(note)
  }, [note, onOpenView])

  const handleActionMenuClick = useCallback(
    (event: React.MouseEvent) => {
      onOpenActionMenu(event, note)
    },
    [note, onOpenActionMenu]
  )

  return (
    <NoteListItem
      note={note}
      showDivider={notesLength - 1 !== index}
      sx={itemSx}
      type={WORK_ORDER}
      onActionMenuClick={handleActionMenuClick}
      onViewClick={handleViewClick}
    />
  )
}

const WONotes: React.FC<WONotesProps> = ({ notes = [], workOrderId = '', noNotesText = T.NO_NOTES_V2, sx, itemSx }) => {
  const [isOpenAddUpdateNoteDialog, setIsOpenAddUpdateNoteDialog] = useState(false)
  const [isOpenViewNoteDialog, setIsOpenViewNoteDialog] = useState(false)
  const [isOpenNoteActionMenu, setIsOpenNoteActionMenu] = useState<HTMLElement | null>(null)
  const [selectedNote, setSelectedNote] = useState<NoteRecord | null>(null)

  const hasNotes = notes.length > 0

  const handleOpenUpdateWorkOrderNote = useCallback(() => {
    setIsOpenAddUpdateNoteDialog(true)
  }, [])

  const handleOpenViewNoteDialog = useCallback((note: NoteRecord) => {
    setSelectedNote(note)
    setIsOpenViewNoteDialog(true)
  }, [])

  const handleCloseViewNoteDialog = useCallback(() => {
    setSelectedNote(null)
    setIsOpenViewNoteDialog(false)
  }, [])

  const handleOpenNoteActionMenu = useCallback((event: React.MouseEvent, note: NoteRecord) => {
    event.stopPropagation()
    setSelectedNote(note)
    setIsOpenNoteActionMenu(event.currentTarget as HTMLElement)
  }, [])

  const handleCloseNoteActionMenu = useCallback(() => {
    setIsOpenNoteActionMenu(null)
  }, [])

  const handleCloseAddUpdateNote = useCallback(() => {
    setSelectedNote(null)
    setIsOpenAddUpdateNoteDialog(false)
  }, [])

  return (
    <>
      {!hasNotes && <HHSectionPlaceholder sx={{}} title={noNotesText} />}
      {hasNotes && (
        <List sx={{ py: 0, ...sx }}>
          {notes.map((note, index) => (
            <WONoteListItemRow
              key={note.id ?? index}
              index={index}
              itemSx={itemSx}
              note={note}
              notesLength={notes.length}
              onOpenActionMenu={handleOpenNoteActionMenu}
              onOpenView={handleOpenViewNoteDialog}
            />
          ))}
        </List>
      )}
      <AddUpdateNoteDialog
        existingNote={selectedNote ?? undefined}
        id={workOrderId}
        isOpen={isOpenAddUpdateNoteDialog}
        title={selectedNote ? T.WORK_ORDER_NOTE : T.NEW_WORK_ORDER_NOTE}
        useCreateNoteMutation={useCreateWorkOrderNoteMutation}
        useUpdateNoteMutation={useUpdateWorkOrderNoteNewMutation}
        onClose={handleCloseAddUpdateNote}
      />
      <ViewNoteDialog
        id={workOrderId}
        isOpen={isOpenViewNoteDialog}
        selectedNote={selectedNote ?? undefined}
        title={T.WORK_ORDER_NOTE}
        useLazyGetNoteHistoryQuery={useLazyGetWorkOrderNoteHistoryQuery}
        onClose={handleCloseViewNoteDialog}
      />
      <NoteActionMenu
        anchorEl={isOpenNoteActionMenu}
        id={workOrderId}
        selectedNote={selectedNote ?? undefined}
        useDeleteNoteMutation={useDeleteWorkOrderNoteMutation}
        usePatchNoteMutation={usePatchWorkOrderNoteMutation}
        onClose={handleCloseNoteActionMenu}
        onEditClick={handleOpenUpdateWorkOrderNote}
      />
    </>
  )
}

export default WONotes
